import { useMemo, useState } from "react";
import { formatWeight, formatNumber } from "../utils/format.js";
import { buildExerciseDetail } from "../utils/exerciseStats.js";
import styles from "./ExerciseList.module.css";

const TOP_N = 10;

// How many sessions the detail lists before asking; an exercise trained twice a
// week fills a phone screen with the rest.
const SESSION_PREVIEW = 6;

// PRs sourced from Hevy's own per-set markers (personalRecords[].type). They span
// every imported workout, not just the selected period — but the import is capped,
// so "all-time" only holds within what we fetched.
const RECORD_DEFS = [
  { key: "best_weight", label: "Highest weight" },
  { key: "best_1rm", label: "Best est. 1RM" },
  { key: "best_volume", label: "Best set volume" },
];

// Our own bests, read off the imported sets rather than Hevy's markers — the two
// answer different questions, so the panel labels and spaces them apart.
const BEST_DEFS = [
  { key: "weight", label: "Heaviest set" },
  { key: "reps", label: "Most reps" },
  { key: "oneRepMax", label: "Best est. 1RM" },
];

// Direction is carried by the arrow and the signed percentage, never by colour
// alone. `text` is the colour-only variant for the expanded explanation.
const TREND_STYLE = {
  progressing: { arrow: "↗", label: "Progressing", badge: "badgeUp", text: "textUp" },
  plateau:     { arrow: "→", label: "Plateau",     badge: "badgeFlat", text: "textFlat" },
  declining:   { arrow: "↘", label: "Declining",   badge: "badgeDown", text: "textDown" },
};

function plural(count, word) {
  return `${formatNumber(count)} ${word}${count === 1 ? "" : "s"}`;
}

function trendPct(trend) {
  const pct = trend.change * 100;
  const shown = pct.toFixed(1);
  // A signed "+0.0%" reads as a gain that rounded away; "±" says it moved by
  // less than the display can show.
  if (Number(shown) === 0) return "±0.0%";
  return `${pct > 0 ? "+" : ""}${shown}%`;
}

// Says what the badge is built from, so a plateau claim can be checked rather
// than just believed. Mirrors the expanded copy, for pointer users who hover.
function trendTitle(trend) {
  return `${TREND_STYLE[trend.status].label} — estimated 1RM ${trendPct(trend)} over the last ${trend.sessions} sessions (${trend.spanDays} days), across all imported history rather than the selected period`;
}

function fmtRecordDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return "";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(d);
}

function isNewThisPeriod(rec, period) {
  if (!rec?.date || !period?.start || !period?.endExclusive) return false;
  const day = String(rec.date).slice(0, 10);
  return day >= period.start && day < period.endExclusive;
}

// "BW" for a weight of 0 — the same vocabulary the workout log uses for a set
// logged at bodyweight.
function setLabel(set, weightUnit) {
  const weight = set.weight > 0 ? formatWeight(set.weight, weightUnit) : "BW";
  return `${weight} × ${formatNumber(set.reps)}`;
}

function RecordDate({ date, workoutId, onOpenWorkout }) {
  if (!workoutId) return <span className={styles.recordDate}>{fmtRecordDate(date)}</span>;
  return (
    <button type="button" className={styles.recordLink} onClick={() => onOpenWorkout?.(workoutId)}>
      {fmtRecordDate(date)} ↗
    </button>
  );
}

function bestValue(key, best, weightUnit) {
  if (key === "reps") {
    return (
      <>
        {formatNumber(best.reps)}
        <span className={styles.recordSub}>
          reps {best.weight > 0 ? `at ${formatWeight(best.weight, weightUnit)}` : "at bodyweight"}
        </span>
      </>
    );
  }
  if (key === "oneRepMax") {
    return (
      <>
        {formatWeight(best.oneRepMax, weightUnit)}
        <span className={styles.recordSub}>from {setLabel(best, weightUnit)}</span>
      </>
    );
  }
  return (
    <>
      {formatWeight(best.weight, weightUnit)}
      <span className={styles.recordSub}>× {formatNumber(best.reps)}</span>
    </>
  );
}

function SessionRow({ session, weightUnit, onOpenWorkout }) {
  const summary = [
    plural(session.setCount, "set"),
    session.topSet ? `top ${setLabel(session.topSet, weightUnit)}` : null,
    session.volumeKg > 0 ? formatWeight(session.volumeKg, weightUnit) : null,
  ].filter(Boolean).join(" · ");

  const body = (
    <>
      <span className={styles.sessionDate}>{fmtRecordDate(session.date)}</span>
      <span className={styles.sessionName}>{session.workoutName || "Workout"}</span>
      <span className={styles.sessionSummary}>
        {summary}
        {session.prSetCount > 0 && (
          <span className={styles.sessionPr}>★ {plural(session.prSetCount, "PR")}</span>
        )}
      </span>
    </>
  );

  // Jumping to the workout needs its id; an older import that never carried one
  // still lists the session, it just does not link.
  if (!session.workoutId) return <div className={styles.sessionRow}>{body}</div>;
  return (
    <button
      type="button"
      className={`${styles.sessionRow} ${styles.sessionLink}`}
      onClick={() => onOpenWorkout?.(session.workoutId)}
    >
      {body}
    </button>
  );
}

function ExerciseDetail({ ex, workouts, weightUnit, period, onOpenWorkout }) {
  const [showAllSessions, setShowAllSessions] = useState(false);
  const detail = useMemo(() => buildExerciseDetail(workouts, ex.name), [workouts, ex.name]);

  const trendStyle = ex.trend ? TREND_STYLE[ex.trend.status] : null;
  const records = ex.records || {};
  const shownRecords = RECORD_DEFS.filter((def) => records[def.key]);
  const shownBests = BEST_DEFS.filter((def) => detail.bests[def.key]);
  const sessions = showAllSessions ? detail.sessions : detail.sessions.slice(0, SESSION_PREVIEW);

  return (
    <div className={styles.details}>
      {trendStyle && (
        <p className={styles.trendNote}>
          <span className={`${styles.trendNoteHead} ${styles[trendStyle.text]}`}>
            {trendStyle.arrow} {trendStyle.label}
          </span>{" "}
          — estimated 1RM {trendPct(ex.trend)} over the last {ex.trend.sessions} sessions
          ({ex.trend.spanDays} days), across all imported history rather than the selected period.
        </p>
      )}

      {ex.prSetCount > 0 && (
        <p className={styles.trendNote}>
          <span className={`${styles.trendNoteHead} ${styles.textPr}`}>
            ★ {plural(ex.prSetCount, "PR")}
          </span>{" "}
          — personal-record sets logged in the selected period.
        </p>
      )}

      {shownBests.length > 0 && (
        <div className={styles.records}>
          <div className={styles.recordCaption}>
            Bests — across {plural(detail.sessionCount, "imported session")}
          </div>
          {shownBests.map((def) => {
            const best = detail.bests[def.key];
            return (
              <div key={def.key} className={styles.recordRow}>
                <span className={styles.recordLabel}>
                  {def.label}
                  {isNewThisPeriod(best, period) && <span className={styles.newBadge}>NEW</span>}
                </span>
                <span className={styles.recordValue}>{bestValue(def.key, best, weightUnit)}</span>
                <RecordDate date={best.date} workoutId={best.workoutId} onOpenWorkout={onOpenWorkout} />
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.records}>
        {shownRecords.length === 0 ? (
          // A set Hevy flagged as a PR need not carry one of the three markers we
          // read, so "1 PR" above and "no markers" here can both be true.
          <div className={styles.recordEmpty}>
            {ex.prSetCount > 0
              ? "No highest-weight, 1RM or set-volume marker among these sets"
              : "No Hevy PR markers in the imported workouts"}
          </div>
        ) : (
          <>
            <div className={styles.recordCaption}>Hevy personal records</div>
            {shownBests.length > 0 && (
              <p className={styles.note}>
                Hevy flags these against the athlete's full history; the bests above only see the
                workouts we imported, so the two can name different sets.
              </p>
            )}
            {shownRecords.map((def) => {
              const rec = records[def.key];
              const isNew = isNewThisPeriod(rec, period);
              return (
                <div key={def.key} className={styles.recordRow}>
                  <span className={styles.recordLabel}>
                    {def.label}
                    {isNew && <span className={styles.newBadge}>NEW</span>}
                  </span>
                  <span className={styles.recordValue}>
                    {def.key === "best_volume" ? (
                      <>
                        {/* Hevy's set volume = weight × reps; show the actual set. */}
                        {formatWeight(rec.weight, weightUnit)}
                        <span className={styles.recordSub}>× {formatNumber(rec.reps)}</span>
                      </>
                    ) : (
                      formatWeight(rec.value, weightUnit)
                    )}
                  </span>
                  <RecordDate date={rec.date} workoutId={rec.workoutId} onOpenWorkout={onOpenWorkout} />
                </div>
              );
            })}
          </>
        )}
      </div>

      {detail.sessionCount > 0 && (
        <div className={styles.sessions}>
          <div className={styles.recordCaption}>
            Session history — {plural(detail.setCount, "set")}
          </div>
          {sessions.map((session, i) => (
            <SessionRow
              key={session.workoutId || `${session.date}-${i}`}
              session={session}
              weightUnit={weightUnit}
              onOpenWorkout={onOpenWorkout}
            />
          ))}
          {detail.sessionCount > SESSION_PREVIEW && (
            <button
              type="button"
              className={styles.moreBtn}
              onClick={() => setShowAllSessions((v) => !v)}
              aria-expanded={showAllSessions}
            >
              {showAllSessions ? "Show fewer" : `Show all ${formatNumber(detail.sessionCount)} sessions`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ExerciseRow({ ex, rank, sortMode, barPct, weightUnit, period, workouts, onOpenWorkout }) {
  const [open, setOpen] = useState(false);

  // The value the bar is drawn from leads; the counts it is *not* drawn from
  // follow it muted. Neither repeats the other — the old layout printed the
  // workout count twice, which cost the name its width on a phone.
  const byWorkouts = sortMode === "workouts";
  const hasVolume = ex.volumeKg > 0;
  const metric = byWorkouts
    ? plural(ex.workoutCount, "workout")
    : hasVolume
      ? formatWeight(ex.volumeKg, weightUnit)
      : plural(ex.setCount, "set");
  const metricSub = byWorkouts
    ? plural(ex.setCount, "set")
    : hasVolume
      ? `${plural(ex.setCount, "set")} · ${plural(ex.workoutCount, "workout")}`
      : plural(ex.workoutCount, "workout");

  const trendStyle = ex.trend ? TREND_STYLE[ex.trend.status] : null;

  return (
    <li className={styles.row}>
      <span className={styles.rank}>{rank}</span>
      <div className={styles.info}>
        <button className={styles.rowToggle} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <div className={styles.infoTop}>
            <strong className={styles.name}>{ex.name}</strong>
            <span className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`} aria-hidden="true">›</span>
          </div>
          <div className={styles.barTrack}>
            <div className={styles.barFill} style={{ width: `${barPct}%` }} />
          </div>
          <div className={styles.meta}>
            <span className={styles.metric}>{metric}</span>
            <span className={styles.metricSub}>{metricSub}</span>
            {ex.prSetCount > 0 && (
              <span
                className={`${styles.badge} ${styles.badgePr}`}
                title={`${plural(ex.prSetCount, "personal-record set")} in this period`}
              >
                <span aria-hidden="true">★</span>
                {plural(ex.prSetCount, "PR")}
              </span>
            )}
            {trendStyle && (
              <span className={`${styles.badge} ${styles[trendStyle.badge]}`} title={trendTitle(ex.trend)}>
                <span aria-hidden="true">{trendStyle.arrow}</span>
                1RM {trendPct(ex.trend)}
                <span className={styles.badgeReader}> — {trendStyle.label}</span>
              </span>
            )}
          </div>
        </button>

        {open && (
          <ExerciseDetail
            ex={ex}
            workouts={workouts}
            weightUnit={weightUnit}
            period={period}
            onOpenWorkout={onOpenWorkout}
          />
        )}
      </div>
    </li>
  );
}

export default function ExerciseList({ exercises, weightUnit, period, workouts, onOpenWorkout }) {
  const [sortMode, setSortMode] = useState("workouts");

  const sorted = [...(exercises || [])].sort((a, b) => {
    if (sortMode === "workouts") {
      return b.workoutCount - a.workoutCount || b.setCount - a.setCount || a.name.localeCompare(b.name);
    }
    return b.volumeKg - a.volumeKg || b.setCount - a.setCount || a.name.localeCompare(b.name);
  });

  const top = sorted.slice(0, TOP_N);
  const max = sortMode === "workouts"
    ? (top[0]?.workoutCount || 0)
    : (top[0]?.volumeKg || 0);

  return (
    <div className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.label}>Top exercises</span>
        <div className={styles.toggle}>
          <button
            className={`${styles.toggleBtn} ${sortMode === "workouts" ? styles.toggleActive : ""}`}
            onClick={() => setSortMode("workouts")}
          >
            Workouts
          </button>
          <button
            className={`${styles.toggleBtn} ${sortMode === "volume" ? styles.toggleActive : ""}`}
            onClick={() => setSortMode("volume")}
          >
            Volume
          </button>
        </div>
      </div>

      {top.length === 0 ? (
        <div className={styles.empty}>No exercise data for this period</div>
      ) : (
        <ol className={styles.list}>
          {top.map((ex, i) => {
            const barVal = sortMode === "workouts" ? ex.workoutCount : ex.volumeKg;
            const barPct = max > 0 ? (barVal / max) * 100 : 0;
            return (
              <ExerciseRow
                key={ex.name}
                ex={ex}
                rank={i + 1}
                sortMode={sortMode}
                barPct={barPct}
                weightUnit={weightUnit}
                period={period}
                workouts={workouts}
                onOpenWorkout={onOpenWorkout}
              />
            );
          })}
        </ol>
      )}
    </div>
  );
}
